import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { RankingTable } from './RankingTable';

import { ArrowDateSelector } from '@/components/GenericsTournaments/ArrowDateSelector';
import { Tabs, DefaultTabsProps } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTourney from '@/hooks/useTourney';
import {
  DatosParticipanteMiniLigasTO,
  PartidoMiniLigaTO,
  ResponseVerML,
} from '@/types/minileagues';
import { FADE_IN_OUT, GENERICS_WORDS } from '@/utils';

export const RankingSelector = () => {
  const { tab, handleTabChanges } = useHandleChangesTabs();
  const { isDesktop } = useIsDesktop();
  const [dataTable, setDataTable] = useState<
    DatosParticipanteMiniLigasTO[] | PartidoMiniLigaTO[] | undefined
  >([]);
  const [variant, setVariant] = useState(GENERICS_WORDS.fixture);
  const { tourneyData, updateTourney } = useTourney();

  const data: ResponseVerML | undefined = useMemo(() => {
    const minileagues = tourneyData.tourney;
    return minileagues;
  }, [tourneyData]);

  const getMatchDay = useCallback((): PartidoMiniLigaTO[] | undefined => {
    if (data?.partidosFecha && tourneyData.matchDay?.orden)
      for (
        let index = 0;
        index < Object.values(data?.partidosFecha).length;
        index++
      ) {
        const element = data?.partidosFecha[index];
        const matchDayArray = element.filter(
          (item) => item.fechaML?.ordF === tourneyData.matchDay?.orden
        );
        if (matchDayArray.length !== 0) {
          return matchDayArray;
        }
      }
  }, [data?.partidosFecha, tourneyData.matchDay?.orden]);

  // // init and handle matchDays
  useEffect(() => {
    if (tab === TabType.FirstTab && tourneyData.matchDay) {
      setDataTable(getMatchDay());
    } else {
      setDataTable(data?.miniliga?.partis || []);
    }

    updateTourney({ subMainTab: tab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.matchDay, tab, getMatchDay, data?.miniliga?.partis]);

  // reseteo tab secundario si cambia tab principal
  useEffect(() => {
    handleTabChanges(tourneyData.mainTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.mainTab]);

  // cambio variante de tabla
  useEffect(() => {
    if (tab === TabType.FirstTab) {
      setVariant(GENERICS_WORDS.fixture);
    } else {
      setVariant(GENERICS_WORDS.positions);
    }
  }, [tab, setVariant]);

  const tabs: DefaultTabsProps['tabs'] = [
    { name: GENERICS_WORDS.fixture },
    { name: GENERICS_WORDS.positions },
  ];

  return (
    <>
      {/* tabs de fecha o torneo */}
      {isDesktop && (
        <div className="text-center lg:pt-6">
          <Tabs
            activeTab={tab}
            className="mt-2 lg:max-w-none lg:h-[38px] lg:mt-0 lg:justify-center lg:w-full"
            classNameTabs="lg:max-w-[200px]"
            tabs={tabs}
            variant="underline"
            onClick={handleTabChanges}
          />
        </div>
      )}

      {/* selector de fechas */}
      <motion.div
        key={tab}
        animate={'show'}
        className="mt-5"
        initial={'hide'}
        variants={FADE_IN_OUT}
      >
        {tab === TabType.FirstTab && (
          <ArrowDateSelector
            key={
              tourneyData.mainTab ||
              tourneyData.rankingAvailable.filter((item) => item.id).join() ||
              tourneyData.matchDay?.nombre
            } // reseteo selector de fechas si cambia tab primario o nuevo torneo
            matchsDaysAvailables={tourneyData.rankingAvailable}
          />
        )}
        <RankingTable dataTable={dataTable} variant={variant} />
      </motion.div>
    </>
  );
};
